import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import styles from './Segmented.module.css';

/**
 * Two to four mutually exclusive choices, one click each — "required / optional /
 * hidden", "must have / nice to have". A select would hide the choice behind a
 * second click for something decided dozens of times per job (CLAUDE.md §4).
 *
 * A radio group underneath (`role="radiogroup"`), with the arrow keys moving the
 * choice as a native one does (06-qol §2).
 */

const ARROW_STEPS: Readonly<Record<string, 1 | -1>> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: string;
  /** Opt-in: how many sit behind this choice (Redesign Group 7, My interviews' Coming up / Past). */
  readonly count?: number;
}

interface SegmentedProps<T extends string> {
  readonly label: string;
  readonly value: T;
  readonly options: readonly SegmentedOption<T>[];
  readonly onChange: (value: T) => void;
  readonly disabled?: boolean;
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: SegmentedProps<T>): ReactNode {
  const group = useRef<HTMLDivElement>(null);

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const step = ARROW_STEPS[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = options[(index + step + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    group.current?.querySelectorAll<HTMLButtonElement>('button')[options.indexOf(next)]?.focus();
  }

  return (
    <div ref={group} className={styles.group} role="radiogroup" aria-label={label} aria-disabled={disabled || undefined}>
      {options.map((option, index) => {
        const isChecked = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isChecked}
            tabIndex={isChecked ? 0 : -1}
            disabled={disabled}
            className={styles.segment}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {option.label}
            {option.count !== undefined && (
              <>
                {' '}
                <span className={styles.count}>{option.count}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
